import { BILLING_PERIOD_LABELS } from "@station/contracts";
import { Alert, Button, MoneyText, SkeletonRows, StatusBadge, useToast } from "@station/ui";
import { useMutation } from "@tanstack/react-query";
import { createFileRoute, Link } from "@tanstack/react-router";
import { type ReactElement, useState } from "react";
import { useTranslation } from "react-i18next";
import { ReasonActionDialog } from "../../../features/platform/billing-dialogs.js";
import {
  usePlatformInvoice,
  usePlatformPermissions,
  useRefreshPlatform,
} from "../../../features/platform/queries.js";
import { EdocumentBadge, Field, InvoiceStateBadge, PlatformPage } from "../../../features/platform/shared.js";
import { InvoiceLines, Section, Totals } from "../../../features/platform/tenant-tabs.js";
import { api } from "../../../lib/api.js";
import { errorMessage } from "../../../lib/errors.js";
import { formatDate } from "../../../lib/format.js";

export const Route = createFileRoute("/platform/faturalar/$id")({ component: InvoiceDetailRoute });

/**
 * Platform faturası ayrıntısı (Prompt 24 web): satırlar (istasyon aboneliği + kullanım, indirimler), KDV, tahsilat
 * eşleşmeleri, gönderilen hatırlatmalar, yasal e-belgenin durumu (hata → yeniden dene). Ödeme almamış ve yasal faturası
 * kesilmemiş fatura iptal edilebilir (neden zorunlu, step-up); kesinleşmiş fatura silinmez.
 */
function InvoiceDetailRoute(): ReactElement {
  const { t } = useTranslation();
  const toast = useToast();
  const refresh = useRefreshPlatform();
  const { id } = Route.useParams();
  const permissions = usePlatformPermissions();
  const canManage = permissions.has("platform.billing.manage");
  const invoice = usePlatformInvoice(id);
  const [voiding, setVoiding] = useState(false);
  const retry = useMutation({
    mutationFn: async () =>
      (await api.POST("/platform/invoices/{id}/retry-edocument", { params: { path: { id } } })).data!,
    onSuccess: async (result) => {
      await refresh();
      toast({
        tone: result.edocumentStatus === "CREATED" ? "success" : "danger",
        title:
          result.edocumentStatus === "CREATED"
            ? t("platform.invoices.edocumentCreated")
            : t("platform.invoices.edocumentFailed"),
        ...(result.edocumentError ? { description: result.edocumentError } : {}),
      });
    },
    onError: (caught) => toast({ tone: "danger", title: errorMessage(caught) }),
  });
  const data = invoice.data;
  const canVoid =
    canManage &&
    data &&
    data.status === "ISSUED" &&
    data.paidAmount === "0" &&
    data.edocumentStatus !== "CREATED";
  const canRetry =
    canManage &&
    data &&
    data.status !== "VOID" &&
    (data.edocumentStatus === "FAILED" || data.edocumentStatus === "NOT_CONFIGURED");

  return (
    <PlatformPage
      title={
        data ? t("platform.invoices.detailTitle", { no: data.invoiceNo }) : t("platform.invoices.detail")
      }
      breadcrumb={[
        {
          label: t("platform.nav.invoices"),
          link: (children, className) => (
            <Link to="/platform/faturalar" className={className}>
              {children}
            </Link>
          ),
        },
        { label: data?.invoiceNo ?? "…" },
      ]}
      permission="platform.billing.read"
      actions={
        <>
          {canRetry ? (
            <Button variant="outline" loading={retry.isPending} onClick={() => retry.mutate()}>
              {t("platform.invoices.retry")}
            </Button>
          ) : null}
          {canVoid ? (
            <Button variant="destructive" onClick={() => setVoiding(true)}>
              {t("platform.invoices.void")}
            </Button>
          ) : null}
        </>
      }
    >
      {invoice.error ? (
        <Alert tone="danger">{errorMessage(invoice.error)}</Alert>
      ) : invoice.isPending || !data ? (
        <SkeletonRows rows={6} />
      ) : (
        <>
          <Section title={t("platform.invoices.summary")}>
            <dl className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
              <Field label={t("platform.fields.tenant")}>
                <Link
                  to="/platform/kiracilar/$id"
                  params={{ id: data.tenant.id }}
                  search={{ tab: "invoices" }}
                  className="font-medium text-primary underline-offset-4 hover:underline"
                >
                  {data.tenant.name}
                </Link>
              </Field>
              <Field label={t("platform.fields.status")}>
                <InvoiceStateBadge invoice={data} />
              </Field>
              <Field label={t("platform.fields.period")}>
                {formatDate(data.periodStart)} – {formatDate(data.periodEnd)}
              </Field>
              <Field label={t("platform.fields.plan")}>
                {data.planCode} · {BILLING_PERIOD_LABELS[data.billingPeriod]}
              </Field>
              <Field label={t("platform.fields.issueDate")}>{formatDate(data.issueDate)}</Field>
              <Field label={t("platform.fields.dueDate")}>{formatDate(data.dueDate)}</Field>
              <Field label={t("platform.fields.billableStations")}>{data.billableStations}</Field>
              <Field label={t("platform.fields.outstanding")}>
                <MoneyText value={data.outstanding} />
              </Field>
              <Field label={t("platform.fields.edocument")}>
                <span className="flex flex-wrap items-center gap-1">
                  <EdocumentBadge status={data.edocumentStatus} />
                  {data.billingInvoiceNo ? (
                    <span className="font-mono text-xs">{data.billingInvoiceNo}</span>
                  ) : null}
                </span>
              </Field>
              <Field label={t("platform.fields.reminders")}>
                {data.remindersSent.length === 0
                  ? "—"
                  : data.remindersSent
                      .map((day) => t("platform.invoices.reminderDay", { count: day }))
                      .join(", ")}
              </Field>
              {data.voidReason ? (
                <Field label={t("platform.fields.voidReason")}>{data.voidReason}</Field>
              ) : null}
            </dl>
            {data.edocumentError ? <Alert tone="danger">{data.edocumentError}</Alert> : null}
          </Section>
          <Section title={t("platform.invoices.lines")}>
            <InvoiceLines lines={data.lines} />
            <Totals
              net={data.netAmount}
              vat={data.vatAmount}
              total={data.totalAmount}
              vatRate={data.vatRate}
            />
          </Section>
          <Section title={t("platform.payments.title")}>
            {data.payments.length === 0 ? (
              <p className="text-sm text-muted-foreground">{t("platform.payments.none")}</p>
            ) : (
              <ul className="grid gap-2 text-sm">
                {data.payments.map((payment) => (
                  <li key={payment.paymentId} className="flex flex-wrap items-center gap-2">
                    <span className="tabular">{formatDate(payment.paidOn)}</span>
                    <MoneyText value={payment.amount} />
                    {payment.reversed ? <StatusBadge>{t("platform.payments.reversed")}</StatusBadge> : null}
                  </li>
                ))}
              </ul>
            )}
          </Section>
        </>
      )}
      {voiding && data ? (
        <ReasonActionDialog
          open
          onOpenChange={setVoiding}
          title={t("platform.invoices.voidTitle", { no: data.invoiceNo })}
          description={t("platform.invoices.voidDescription")}
          submitLabel={t("platform.invoices.void")}
          successTitle={t("platform.invoices.voided")}
          run={(reason) =>
            api.POST("/platform/invoices/{id}/void", {
              params: { path: { id } },
              body: { reason, version: data.version },
            })
          }
        />
      ) : null}
    </PlatformPage>
  );
}
